<template>
  <div class="page-main flex-page">
    <!--START: Header Wrapper-->
    <div
      class="header-wrapper"
      :class="{ 'show-shadow': showOptions.headerShadow }"
    >
      <!--START: Page Header-->
      <RegularHeader
        class="regular-header"
        :data="regularHeader"
      ></RegularHeader>
      <!--END: Page Header-->
    </div>
    <!--END: Header Wrapper-->

    <!--START: Loader-->
    <PageLoader
      v-if="lodash.isEmpty(company) || showOptions.pageLoader"
      :showLoader="true"
    ></PageLoader>
    <!--END: Loader-->

    <div v-else ref="scrollWrapper" class="scroll-main" @scroll="contentScroll">
      <!--START: Subscription Info-->
      <AiAssistantSubscription
        :subscription="currentSubscription"
        :chargebeeSubscription="chargebeeSubscription"
        @manageSubscription="manageSubscription"
        @subscribePlan="subscribePlan"
      ></AiAssistantSubscription>
      <!--END: Subscription Info-->

      <!--START: License Management-->
      <div
        v-if="showLicenseManagement"
        class="license-section"
      >
        <Licenses
          :subscription="currentSubscription"
          @manageLicenses="openLicenseManager"
        ></Licenses>
      </div>
      <!--END: License Management-->
    </div>

    <!--START: License Manager Modal-->
    <LicenseManager
      v-if="showOptions.licenseManager"
      :subscription="currentSubscription"
      @updateLicenses="updateLicenses"
      @closeModal="closeLicenseManager"
    ></LicenseManager>
    <div
      class="bg-overlay light"
      :class="{ show: showOptions.licenseManager }"
      @click="closeLicenseManager"
    ></div>
    <!--END: License Manager Modal-->

    <!--START: Loader-->
    <LineLoader :show="showOptions.lineLoader"></LineLoader>
    <!--END: Loader-->

    <!--START: Status Message-->
    <StatusMessage
      :show="status.show"
      :status="status.status"
      :title="status.title"
    ></StatusMessage>
    <!--END: Status Message-->
  </div>
</template>

<script>
// Import libraries
import _ from "lodash";

// Importing components
import RegularHeader from "@/components/dashboard/RegularHeader";
import AiAssistantSubscription from "@/components/dashboard/subscriptions/AiAssistant/Index";
import Licenses from "@/components/dashboard/subscriptions/AiAssistant/Licenses";
import LicenseManager from "@/components/dashboard/subscriptions/AiAssistant/LicenseManager";
import LineLoader from "@/components/loaders/LineLoader";
import PageLoader from "@/components/loaders/PageLoader";
import StatusMessage from "@/components/modals/StatusMessage";

// Importing Services
import { SubscriptionService, AiAssistantServices } from "@/services";
const { AgentService } = AiAssistantServices;

import "external-svg-loader";

export default {
  name: "Subscription",
  data() {
    return {
      lodash: _,
      selectedProduct: "aiAssistant",
      showOptions: {
        headerShadow: false,
        lineLoader: false,
        pageLoader: false,
        licenseManager: false,
      },
      currentSubscription: {},
      chargebeeSubscription: {},
      regularHeader: {
        title: "Subscriptions & Licenses",
        description: "Manage your subscription and assign licenses to agents",
      },
      status: {
        show: false,
        status: "success",
        title: "",
      },
    };
  },
  props: {},
  watch: {},
  components: {
    RegularHeader,
    AiAssistantSubscription,
    Licenses,
    LicenseManager,
    LineLoader,
    PageLoader,
    StatusMessage,
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    // Show license management for active subscriptions with license quantity
    // Legacy companies with license quantity should also see this
    showLicenseManagement() {
      const subscription = this.currentSubscription;
      if (!subscription) return false;

      // Show for any active subscription with licenses purchased
      // This includes legacy companies that purchased licenses
      return (
        subscription.status === "active" &&
        subscription.licenses?.quantity > 0
      );
    },
  },
  async created() {
    this.getActiveSubscription();
  },
  mounted() {
    this.addChargebeeScript();
  },
  methods: {
    // Get the active subscription
    async getActiveSubscription() {
      const data = { product: this.selectedProduct };
      this.currentSubscription =
        this.company.products[this.selectedProduct].subscription;
      const response = await SubscriptionService.GetActiveSubscription(data);
      if (!response.hasError) {
        this.chargebeeSubscription = response.data;
      }
    },

    // Reload the subscription view after the Chargebee portal is close
    async reloadSubscription() {
      this.showOptions.pageLoader = true;
      await this.getUser();
      this.currentSubscription =
        this.company.products[this.selectedProduct].subscription;
      this.showOptions.pageLoader = false;
    },

    // Add Chargebee script to header
    addChargebeeScript() {
      let chargebeeScript = document.createElement("script");
      chargebeeScript.setAttribute(
        "src",
        "https://js.chargebee.com/v2/chargebee.js"
      );
      document.head.appendChild(chargebeeScript);
    },

    // Subscribe to the plan
    async subscribePlan() {
      this.showOptions.lineLoader = true;
      const response = await SubscriptionService.GetSubscriptionHostedPage({
        product: this.selectedProduct,
        subscriptionType:
          this.currentSubscription.planType === "trial" ? "new" : "change",
      });
      this.showOptions.lineLoader = false;

      if (response.data === "subscription-changed") {
        this.reloadSubscription();
      } else {
        this.chargebeeInstance = window.Chargebee.init({
          site: process.env.VUE_APP_CHARGEBEE_SITE,
          publishableKey: process.env.VUE_APP_CHARGEBEE_PUBLISHABLE_KEY,
        });

        const ele = this;
        let chargebeeInstance = window.Chargebee.getInstance();
        chargebeeInstance.openCheckout({
          hostedPage: function () {
            return new Promise(function (resolve) {
              resolve(response.data.hosted_page);
            });
          },

          success: async function () {
            ele.reloadSubscription();
          },
        });
      }
    },

    // Manage subscription
    async manageSubscription() {
      this.showOptions.lineLoader = true;
      const data = { product: this.selectedProduct };
      const response = await SubscriptionService.GetPortalSession(data);
      this.showOptions.lineLoader = false;

      this.chargebeeInstance = window.Chargebee.init({
        site: process.env.VUE_APP_CHARGEBEE_SITE,
        publishableKey: process.env.VUE_APP_CHARGEBEE_PUBLISHABLE_KEY,
      });

      const chargebeeInstance = window.Chargebee.getInstance();
      chargebeeInstance.setPortalSession(function () {
        return new Promise(function (resolve) {
          resolve(response.data);
        });
      });

      let cbPortal = chargebeeInstance.createChargebeePortal();
      const ele = this;
      cbPortal.open({
        close() {
          ele.reloadSubscription();
        },
      });
    },

    // Open license manager modal
    openLicenseManager() {
      this.showOptions.licenseManager = true;
    },

    // Close license manager modal
    closeLicenseManager() {
      this.showOptions.licenseManager = false;
    },

    // Update licenses
    async updateLicenses(agents) {
      this.showOptions.lineLoader = true;
      this.closeLicenseManager();

      const response = await AgentService.UpdateLicenses({
        agents: agents.map(({ zendeskUserId, name, email, role }) => ({
          zendeskUserId,
          name,
          email,
          role,
        })),
      });

      this.showOptions.lineLoader = false;

      if (!response.hasError) {
        // Update local subscription data
        await this.reloadSubscription();
        this.status.status = "success";
        this.status.title = "Licenses updated successfully";
      } else {
        this.status.status = "error";
        this.status.title = response.message || "Failed to update licenses";
      }

      this.showStatusMessage(this.status, 3000);
    },

    // Event on sourcesScroll scroll
    contentScroll() {
      const scrollTop = this.$refs.scrollWrapper.scrollTop;

      // Check if header needs shadow
      if (scrollTop > 0) this.showOptions.headerShadow = true;
      else this.showOptions.headerShadow = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";
.header-wrapper {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e9e6ef;
  margin-bottom: 0 !important;
  z-index: 2;

  .regular-header {
    flex: 1;
    max-width: 70%;
    margin-right: 30%;
  }
}

.scroll-main {
  flex: 1;
  padding: 2rem 1.5rem;
  overflow-y: scroll;
}

.license-section {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #e9e6ef;
}

.page-loader-wrapper {
  margin-top: 2rem;
}

/deep/ .line-loader {
  position: fixed;
  z-index: 101;
  bottom: 0;
  left: 0;
}

.show-shadow {
  box-shadow: 0 0.5rem 0.7rem -0.5rem $boxShadowColor;
}

.bg-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;

  &.show {
    opacity: 1;
    visibility: visible;
  }

  &.light {
    background: rgba(0, 0, 0, 0.3);
  }
}
</style>